import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { UserStore } from "./userStore";
import { UserResponse } from "@/models/core/user";
import { encryptData, decryptData } from "@/utils/encryption";

const encryptedStorage = {
  getItem: (name: string): string | null => {
    const value = localStorage.getItem(name);
    if (!value) return null;
    return decryptData(value);
  },
  setItem: (name: string, value: string): void => {
    const encrypted = encryptData(value);
    localStorage.setItem(name, encrypted);
  },
  removeItem: (name: string): void => {
    localStorage.removeItem(name);
  },
};

type UserType = "student" | "school_admin" | "super_admin" | null;

interface AuthState {
  isLoggedIn: boolean;
  userType: UserType;
  currentUser: UserResponse | null;
  accessToken: string | null;
  refreshToken: string | null;
  _hasHydrated: boolean;

  logout: () => void;
  login: (
    isLoggedIn: boolean,
    userType: UserType,
    currentUser: UserResponse,
    accessToken?: string,
    refreshToken?: string,
  ) => void;
  setTokens: (accessToken: string, refreshToken: string) => void;
  setHasHydrated: (state: boolean) => void;
}

export const AuthStore = create<AuthState>()(
  persist(
    (set) => ({
      isLoggedIn: false,
      userType: null,
      currentUser: null,
      accessToken: null,
      refreshToken: null,
      _hasHydrated: false,

      setHasHydrated: (state: boolean) => {
        set({ _hasHydrated: state });
      },

      setTokens: (accessToken: string, refreshToken: string) => {
        set({ accessToken, refreshToken });
      },

      login: (isLoggedIn, userType, currentUser, accessToken, refreshToken) => {
        set({
          isLoggedIn,
          userType,
          currentUser,
          accessToken: accessToken ?? currentUser.accessToken ?? null,
          refreshToken: refreshToken ?? currentUser.refreshToken ?? null,
        });
      },

      logout: () => {
        try {
          set({
            isLoggedIn: false,
            userType: null,
            currentUser: null,
            accessToken: null,
            refreshToken: null,
          });
          UserStore.getState().clearProfile();
        } catch (error) {
          console.error("Logout error:", error);
        }
      },
    }),
    {
      name: "auth-storage",
      storage: createJSONStorage(() => encryptedStorage),
      // This function runs when the storage is loaded into the store
      onRehydrateStorage: () => (state) => {
        state?.setHasHydrated(true);
      },
    },
  ),
);

export const useAuth = () => {
  const isAuthenticated = AuthStore(
    (state) => state.isLoggedIn
  );

  const isCurrentUser = AuthStore(
    (state) => state.currentUser
  );

  const isHydrated = AuthStore(
    (state) => state._hasHydrated
  );

  const logout = AuthStore(
    (state) => state.logout
  );

  return {
    isAuthenticated,
    isCurrentUser,
    isHydrated,
    logout,
  };
};
